import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const API = window.__RUNTIME_CONFIG__?.apiBase || window.location.origin;

// Public page behind a manager's Renew / Don't renew link in their contract
// expiry email (managers have no Talent Pool login). Their answer is a
// request: the admins are emailed and send the addendum themselves.
const fmt = (iso) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : "";

// Same month arithmetic as the addendum (day clamped to the target month).
const addMonths = (iso, months) => {
  const d = new Date(iso + "T00:00:00");
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), lastDay));
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}-${String(target.getDate()).padStart(2, "0")}`;
};

export default function ContractRenewalDecision() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [choice, setChoice] = useState(searchParams.get("choice") === "no_renew" ? "no_renew" : searchParams.get("choice") === "renew" ? "renew" : "");
  const [months, setMonths] = useState(6);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/auth/contract-renewal/${token}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setError(d.error);
        else {
          setInfo(d);
          setEffectiveDate(d.decision?.effective_date || d.default_effective_date);
          if (d.decision?.extension_months) setMonths(d.decision.extension_months);
          if (d.decision?.note) setNote(d.decision.note);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Could not connect to the server. Please try again later.");
        setLoading(false);
      });
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const body = choice === "renew"
        ? { decision: "renew", extension_months: months, effective_date: effectiveDate, note }
        : { decision: "no_renew", note };
      const res = await fetch(`${API}/api/auth/contract-renewal/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Something went wrong");
      setSaved(d.decision);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const page = { minHeight: "100vh", background: "#f0f4f8", padding: "30px 16px", fontFamily: "system-ui, sans-serif", display: "flex", justifyContent: "center" };
  const card = { background: "white", borderRadius: 16, padding: 32, maxWidth: 560, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", height: "fit-content", boxSizing: "border-box" };
  const pill = (active, colour) => ({
    flex: 1, padding: "12px 10px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 15,
    border: `2px solid ${active ? colour : "#d9e1e7"}`, background: active ? colour : "white", color: active ? "white" : "#1c2b33",
  });
  const label = { display: "block", fontSize: 13, color: "#55656f", margin: "16px 0 6px", fontWeight: 600 };

  if (loading) return <div style={page}><div style={card}>Loading…</div></div>;
  if (!info) return <div style={page}><div style={card}><p style={{ color: "#c0392b", margin: 0 }}>{error}</p></div></div>;

  if (saved) {
    return (
      <div style={page}>
        <div style={card}>
          <h2 style={{ marginTop: 0, color: "#2c1e3a" }}>Thank you</h2>
          <p>
            {saved.decision === "renew"
              ? <>You asked to renew <b>{info.candidate_name}</b>'s contract for <b>{saved.extension_months} months</b> from <b>{fmt(saved.effective_date)}</b> (new end date {fmt(addMonths(saved.effective_date, saved.extension_months))}).</>
              : <>You asked <b>not</b> to renew <b>{info.candidate_name}</b>'s contract.</>}
          </p>
          <p style={{ color: "#55656f" }}>The Talent Pool admins have been emailed. If you change your mind, open this link again before the contract ends.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={card}>
        <h2 style={{ marginTop: 0, color: "#2c1e3a" }}>Contract renewal</h2>
        <p style={{ marginBottom: 4 }}>
          <b>{info.candidate_name}</b>{info.position ? ` — ${info.position}` : ""}{info.project_name ? `, ${info.project_name}` : ""}
        </p>
        <p style={{ color: "#55656f", marginTop: 0 }}>Current contract ends on <b>{fmt(info.current_end_date)}</b>.</p>
        {info.decision && (
          <p style={{ background: "#f4f8fa", padding: "8px 12px", borderRadius: 8, fontSize: 14 }}>
            You already answered: <b>{info.decision.decision === "renew" ? `Renew for ${info.decision.extension_months} months from ${fmt(info.decision.effective_date)}` : "Don't renew"}</b>. You can change it below.
          </p>
        )}

        <span style={label}>Should this contract be renewed?</span>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" style={pill(choice === "renew", "#2e7d32")} onClick={() => setChoice("renew")}>Renew</button>
          <button type="button" style={pill(choice === "no_renew", "#8e2c2c")} onClick={() => setChoice("no_renew")}>Don't renew</button>
        </div>

        {choice === "renew" && (
          <>
            <span style={label}>For how long?</span>
            <div style={{ display: "flex", gap: 10 }}>
              {info.month_options.map((m) => (
                <button key={m} type="button" style={pill(months === m, "#2c1e3a")} onClick={() => setMonths(m)}>{m} months</button>
              ))}
            </div>
            <label style={label} htmlFor="effective">Starting from</label>
            <input id="effective" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
              style={{ padding: "10px 12px", border: "1px solid #d9e1e7", borderRadius: 8, fontSize: 15, width: "100%", boxSizing: "border-box" }} />
            {effectiveDate && (
              <p style={{ fontSize: 14, color: "#2e7d32", margin: "8px 0 0" }}>New end date: <b>{fmt(addMonths(effectiveDate, months))}</b></p>
            )}
          </>
        )}

        {choice && (
          <>
            <label style={label} htmlFor="note">Note for the admins (optional)</label>
            <textarea id="note" rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={choice === "renew" ? "e.g. project extended by the client" : "e.g. project ending"}
              style={{ padding: "10px 12px", border: "1px solid #d9e1e7", borderRadius: 8, fontSize: 14, width: "100%", boxSizing: "border-box", fontFamily: "inherit" }} />
            {error && <p style={{ color: "#c0392b" }}>{error}</p>}
            <button type="button" onClick={submit} disabled={submitting || (choice === "renew" && !effectiveDate)}
              style={{ marginTop: 16, width: "100%", padding: "12px 0", border: "none", borderRadius: 10, background: "#2c1e3a", color: "white", fontWeight: 700, fontSize: 15, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
              {submitting ? "Sending…" : "Send my answer"}
            </button>
          </>
        )}
        <p style={{ fontSize: 12, color: "#8296a1", marginTop: 20 }}>
          This doesn't change the contract by itself — an admin confirms it and sends any extension to the employee to sign.
        </p>
      </div>
    </div>
  );
}
